import React, { useState, useEffect, ReactElement, EffectCallback } from 'react';
import { useParams, useNavigate, NavigateFunction } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { HashTag, Member, Post, TribeTemplate, User } from '../../../types/entity.types';
import { RadioOrSelectOption } from '../../../types/common.types';
import { RootState } from '../../../types/state/root.types';
import { MemberState } from '../../../types/state/member.types';
import { TribeState } from '../../../types/state/tribe.types';

import { generateUUID } from '../../../utils/util';
import { TRIBE_PAGE_SIZE, UPCOMING } from '../../../utils/constants';

import { requestViewProgramme } from '../../../api/programme/programme.actions';
import {
  requestCreateTribeTemplate,
  requestViewTribeTemplate,
  requestUpdateTribeTemplate
} from '../../../api/tribe-template/tribe-template.actions';
import { requestViewTribeMembers, requestViewMemberProfile } from '../../../api/member/member.actions';
import { requestSearchPosts, requestViewPostHierarchy, requestViewPosts } from '../../../api/post/post.actions';
import { requestViewReactions } from '../../../api/reaction/reaction.actions';
import { requestViewApprovalCount } from '../../../api/approval/approval.actions';

import {
  setPosts,
  setPost,
  setPostHierarchy,
  setPostSearchResults,
  setProfile
} from '../../../slices/programme/tribe/tribe.slice';

import Tribe from '../../../components/screens/programme/tribe/Tribe';

const TribeContainer = (): ReactElement => {
  const dispatch: Dispatch = useDispatch();
  const navigate: NavigateFunction = useNavigate();

  const [hashtags, setHashtags] = useState<HashTag[]>([]);
  const [searchType, setSearchType] = useState<RadioOrSelectOption>({ label: 'Newest Posts', value: 'newest-posts' });

  const { tribeTemplate, posts, memberPosts, approvalCount, postHierarchy, postSearchResults, profile } = useSelector(
    ({ tribe }: RootState): TribeState => tribe
  );
  const { members } = useSelector(({ member }: RootState): MemberState => member);
  const user: User | undefined = useSelector(({ system }: RootState): User | undefined => system.user);

  const { status, programmeExternalReference, memberExternalReference } = useParams();

  useEffect((): void => {
    // @ts-expect-error TODO: Fix after adding types to programme actions
    dispatch(requestViewProgramme({ externalReference: programmeExternalReference }));
    // @ts-expect-error TODO: Fix after adding types to tribe template actions
    dispatch(requestViewTribeTemplate({ externalReference: programmeExternalReference }));

    if (status !== UPCOMING) {
      // @ts-expect-error TODO: Fix after adding types to member actions
      dispatch(requestViewTribeMembers({ externalReference: programmeExternalReference }));
      // @ts-expect-error TODO: Fix after adding types to approval actions
      dispatch(requestViewApprovalCount({ programmeExternalReference }));
    }
  }, [dispatch, status, programmeExternalReference]);

  useEffect((): ReturnType<EffectCallback> => {
    if (status !== UPCOMING) {
      dispatch(
        // @ts-expect-error TODO: Fix after adding types to post actions
        requestViewPosts({
          page: 0,
          size: TRIBE_PAGE_SIZE,
          searchType: searchType.value,
          programmeExternalReference,
          memberExternalReference
        })
      );
    }

    return (): void => {
      dispatch(setPosts([]));
    };
  }, [dispatch, programmeExternalReference, status, searchType, memberExternalReference]);

  useEffect((): void => {
    if (members.length > 0 && user && status !== UPCOMING) {
      const member: Member | undefined = members.find((member: Member): boolean => member.username === user.username);
      // @ts-expect-error TODO: Fix after adding types to reaction actions
      dispatch(requestViewReactions({ memberExternalReference: member?.memberExternalReference }));
    }
  }, [dispatch, status, members, user]);

  useEffect((): void => {
    if (tribeTemplate?.hashTags) {
      const mappedHashtags: HashTag[] = tribeTemplate.hashTags.map(
        (hashtag: HashTag): HashTag => ({
          id: generateUUID(),
          name: hashtag.text
        })
      );

      setHashtags(mappedHashtags);
    }
  }, [tribeTemplate]);

  useEffect((): void => {
    if (!memberExternalReference) {
      dispatch(setProfile(undefined));
      return;
    }

    // @ts-expect-error TODO: Fix after adding types to member actions
    dispatch(requestViewMemberProfile({ memberExternalReference }));
  }, [dispatch, memberExternalReference]);

  const createHashtag = (): void => {
    const hashtag: HashTag = { id: generateUUID(), name: '', count: 0 };
    setHashtags((prevState: HashTag[]): HashTag[] => [...prevState, hashtag]);
  };

  const updateHashtag = (id: string | number, value: string): void => {
    const copyOfHashtags: HashTag[] = structuredClone(hashtags);
    copyOfHashtags.forEach((hashtag: HashTag): void => {
      if (hashtag.id === id) {
        hashtag.name = value;
      }
    });

    setHashtags(copyOfHashtags);
  };

  const deleteHashtag = (id: string | number): void => {
    setHashtags(hashtags.filter((hashtag: HashTag): boolean => hashtag.id !== id));
  };

  const createTribeTemplate = (tribeTemplate: TribeTemplate, redirectUrl?: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to tribe template actions
      requestCreateTribeTemplate({
        input: {
          tribeTemplate,
          programmeExternalReference
        },
        onSuccess: (): void => {
          if (redirectUrl) {
            navigate(redirectUrl);
          }
        }
      })
    );
  };

  const updateTribeTemplate = (tribeTemplate: TribeTemplate, redirectUrl?: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to tribe template actions
      requestUpdateTribeTemplate({
        input: {
          tribeTemplate,
          programmeExternalReference
        },
        onSuccess: (): void => {
          if (redirectUrl) {
            navigate(redirectUrl);
          }
        }
      })
    );
  };

  const loadPosts = (): void => {
    let page: number;
    if (memberExternalReference) {
      page = Math.floor(memberPosts.length / TRIBE_PAGE_SIZE);
    } else {
      page = Math.floor(posts.length / TRIBE_PAGE_SIZE);
    }

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to post actions
      requestViewPosts({
        programmeExternalReference,
        size: TRIBE_PAGE_SIZE,
        searchType: searchType.value,
        ammend: true,
        page,
        memberExternalReference
      })
    );
  };

  const refreshPost = (postExternalReference: string): void => {
    const post: Post = posts.find((post: Post): boolean => post.externalReference === postExternalReference) as Post;
    const indexOfPost: number = posts.indexOf(post);

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to post actions
      requestViewPosts({
        programmeExternalReference,
        page: indexOfPost,
        size: 1,
        searchType: searchType.value,
        replace: true
      })
    );

    const foundMember: Member | undefined = members.find(
      (member: Member): boolean => member.username === user?.username
    );
    // @ts-expect-error TODO: Fix after adding types to reaction actions
    dispatch(requestViewReactions({ memberExternalReference: foundMember?.memberExternalReference }));
  };

  const clearPost = (): void => {
    dispatch(setPost(undefined));
    dispatch(setPostHierarchy(undefined));
  };

  const executeSearch = (searchValue: string): void => {
    clearPost();
    // @ts-expect-error TODO: Fix after adding types to post actions
    dispatch(requestSearchPosts({ programmeExternalReference, memberExternalReference, searchValue }));
  };

  const handleLoadPostWithHierarchy = (externalReference: string): void => {
    clearPostSearchResults();
    // @ts-expect-error TODO: Fix after adding types to post actions
    dispatch(requestViewPostHierarchy({ externalReference }));
  };

  const clearPostSearchResults = (): void => {
    dispatch(setPostSearchResults(undefined));
  };

  return (
    <Tribe
      status={status as string}
      tribeTemplate={tribeTemplate}
      pendingApprovals={approvalCount}
      posts={posts}
      memberPosts={memberPosts}
      members={members}
      hashtags={hashtags}
      profilePicture={user?.profilePicture as string | undefined}
      searchType={searchType}
      createHashtag={createHashtag}
      updateHashtag={updateHashtag}
      deleteHashtag={deleteHashtag}
      createTribeTemplate={createTribeTemplate}
      updateTribeTemplate={updateTribeTemplate}
      loadPosts={loadPosts}
      refreshPost={refreshPost}
      clearPost={clearPost}
      setSearchType={setSearchType}
      searchResults={postSearchResults}
      clearPostSearchResults={clearPostSearchResults}
      executeSearch={executeSearch}
      loadPostWithHierarchy={handleLoadPostWithHierarchy}
      postWithHierarchy={postHierarchy}
      profile={profile}
    />
  );
};

export default TribeContainer;
